import Head from 'next/head';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';

import { useLandingT, usePageVars } from './kit';
import type { LandingCopy, LandingFaq as Faq } from './pages';

/**
 * The page's own questions first, then the ones every transcription page
 * answers. Rendered as FAQPage structured data too.
 */
export default function LandingFaq({ page }: { page: LandingCopy }) {
  const { t } = useLandingT();
  const vars = usePageVars(page);

  const shared: Faq[] = [
    {
      question: 'Is it free to use?',
      answer:
        'Yes. Every account gets {{free}} free transcriptions a month, with speaker labels, timestamps and subtitle downloads included. Paid plans add more transcriptions, starting at {{starter}} a month.',
    },
    {
      question: 'How long can my recording be?',
      answer:
        'Long recordings are fine: a file counts as one transcription whatever its length. Recordings made with the AI Note Taker can run for up to 2 hours.',
    },
    {
      question: 'Which languages can it transcribe?',
      answer:
        '{{languages}} languages, including English, Spanish, French, German, Portuguese, Chinese, Japanese, Hindi and Arabic. You can pick the language or let it be detected automatically.',
    },
    {
      question: 'Can it tell different speakers apart?',
      answer:
        'Yes. Turn on speaker detection and each line is labelled with its speaker and a timestamp. You can give every speaker a name.',
    },
    {
      question: 'What can I do with the transcript?',
      answer:
        'Copy it, download subtitles as SRT or VTT, share a link, or generate a summary, action items, show notes, an article and more from it. You can also ask questions about the recording in a chat.',
    },
    {
      question: 'Are my recordings private?',
      answer:
        'Yes. Only you can open your transcripts unless you share them, and we don’t use your recordings to train our own models. Easy-Peasy.AI is SOC 2, ISO 27001, GDPR and HIPAA compliant.',
    },
  ];

  const faqs = [
    ...page.faqs.map((faq) => ({
      question: t(faq.question, vars),
      answer: t(faq.answer, vars),
    })),
    ...shared.map((faq) => ({
      question: t(faq.question, vars),
      answer: t(faq.answer, vars),
    })),
  ];

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: { '@type': 'Answer', text: answer },
    })),
  };

  return (
    <section id='faq' className='py-20 sm:py-28'>
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</g, '\\u003c'),
          }}
        />
      </Head>
      <div className='mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-8'>
        <div className='lg:col-span-4'>
          <SectionHeading size='md' title={t('Questions and answers.')} />
        </div>

        <div className='lg:col-span-8'>
          <ul className='divide-y divide-[#0f0f28]/[0.08] border-y border-[#0f0f28]/[0.08] dark:divide-white/[0.08] dark:border-white/[0.08]'>
            {faqs.map(({ question, answer }, index) => (
              <li key={question}>
                <details className='group' open={index === 0}>
                  <summary
                    className={clsxm(
                      'flex cursor-pointer list-none items-center justify-between gap-6 py-6 text-start text-lg font-semibold transition-colors hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600 dark:hover:text-primary-300 [&::-webkit-details-marker]:hidden',
                      tone.ink,
                    )}
                  >
                    <h3 className='text-lg font-semibold'>{question}</h3>
                    <span
                      aria-hidden='true'
                      className={clsxm(
                        'relative flex h-8 w-8 flex-none items-center justify-center rounded-full',
                        tone.line,
                      )}
                    >
                      <span className='absolute h-px w-3 bg-current' />
                      <span className='absolute h-3 w-px bg-current transition-transform duration-300 group-open:rotate-90 group-open:opacity-0' />
                    </span>
                  </summary>
                  <p
                    className={clsxm(
                      'max-w-2xl pb-7 pe-12 text-[16px] leading-7',
                      tone.body,
                    )}
                  >
                    {answer}
                  </p>
                </details>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
